import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { ShoppingCart, Shield, Zap, Package, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductCTA = ({ bacLevel, legalStatus }) => {
  const [product, setProduct] = useState(null);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/product`);
      setProduct(response.data);
    } catch (error) {
      console.error("Error fetching product:", error);
    }
  };

  useEffect(() => {
    fetchProduct();
  }, []);

  if (!product || !product.is_active) return null;

  // Stratejik mesaj seçimi
  const getMessage = () => {
    if (legalStatus === "critical") {
      return {
        title: "⚖️ Kendinizi Koruyun - Bir Daha Asla Bu Durumda Olmayın",
        subtitle: "1.00+ promil cezai soruşturma riski taşır. Alcodot test çubuğu ile araç kullanmadan önce her zaman emin olun. Hayatınızı, ehliyetinizi ve özgürlüğünüzü riske atmayın.",
        urgency: "critical"
      };
    } else if (legalStatus === "illegal") {
      return {
        title: "Bir Dahaki Sefere Emin Olun",
        subtitle: "Alcodot test çubuğu ile anında, kesin ölçüm yapın. Yasal sınırı aşmadan önce bilin.",
        urgency: "high"
      };
    } else if (legalStatus === "border") {
      return {
        title: "Kesin Sonuç İçin Alcodot",
        subtitle: "Sınırdayken tahmin yürütmeyin. Profesyonel ölçüm cihazı ile her zaman emin olun.",
        urgency: "medium"
      };
    } else {
      return {
        title: "Her Sürücünün Yanında Olmalı",
        subtitle: "Alcodot test çubuğu - Araç kullanmadan önce gönül rahatlığı ile kontrol edin.",
        urgency: "low"
      };
    }
  };

  const message = getMessage();
  const urgencyColor = message.urgency === "critical" ? "from-rose-700 to-red-900 animate-pulse" :
                       message.urgency === "high" ? "from-red-500 to-rose-600" : 
                       message.urgency === "medium" ? "from-yellow-500 to-orange-500" :
                       "from-teal-500 to-cyan-600";

  return (
    <div className="glass-card-premium rounded-3xl p-4 sm:p-8 neon-border hover-lift smooth-transition" data-testid="product-cta">
      <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
        {/* Product visual */}
        <div className="flex-shrink-0 mx-auto sm:mx-0">
          {product.image_url ? (
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl overflow-hidden shadow-2xl neon-glow relative">
              <img 
                src={product.image_url} 
                alt="Alcodot Test Çubuğu" 
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const fallback = document.createElement('div');
                  fallback.className = 'w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl flex items-center justify-center shadow-2xl neon-glow';
                  fallback.innerHTML = '<svg class="w-10 h-10 sm:w-12 sm:h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path></svg>';
                  e.currentTarget.parentElement.appendChild(fallback);
                }}
              />
              <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-black px-2 py-1 rounded-full animate-pulse">
                YENİ
              </div>
            </div>
          ) : (
            <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl flex items-center justify-center shadow-2xl neon-glow relative">
              <Shield className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
              <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-black px-2 py-1 rounded-full animate-pulse">
                YENİ
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 w-full">
          <h4 className="text-xl sm:text-2xl font-black text-white mb-2">{message.title}</h4>
          <p className="text-gray-300 text-sm mb-4 leading-relaxed">
            {message.subtitle}
          </p>

          {/* Features */}
          <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-4 sm:mb-5">
            <div className="flex items-center gap-2 text-xs text-teal-400">
              <Zap className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="text-xs">Anında Sonuç</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-teal-400">
              <Shield className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="text-xs">Profesyonel Ölçüm</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-teal-400">
              <Package className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="text-xs">Ücretsiz Kargo</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-teal-400">
              <ShoppingCart className="w-3 h-3 sm:w-4 sm:h-4" />
              <span className="text-xs">Hızlı Teslimat</span>
            </div>
          </div>

          {/* Price & CTA */}
          <div className="flex flex-col sm:flex-row items-center sm:justify-between gap-3 sm:gap-4">
            <div className="text-center sm:text-left">
              <p className="text-xs text-gray-400 mb-1">Özel Fiyat</p>
              <p className="text-2xl sm:text-3xl font-black text-gradient-glow">{product.price} TL</p>
            </div>
            <Button
              onClick={() => window.open(product.product_url, '_blank')}
              className={`w-full sm:w-auto bg-gradient-to-r ${urgencyColor} hover:scale-105 text-white rounded-2xl px-6 sm:px-8 py-4 sm:py-6 font-black text-base sm:text-lg shadow-lg neon-glow smooth-transition shimmer`}
              data-testid="buy-product-btn"
            >
              <ShoppingCart className="w-5 h-5 mr-2" />
              Satın Al
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </div>

      {/* Trust badge */}
      <div className="mt-6 pt-6 border-t border-teal-500/20 text-center">
        <p className="text-xs text-gray-400">
          ⭐ Binlerce sürücünün güvenle kullandığı profesyonel test cihazı
        </p>
      </div>
    </div>
  );
};

export default ProductCTA;